import { App, Form, Input, Modal } from 'antd';
import { useState } from 'react';
import { api, errorMessage } from '../api/client';
import { useAuth } from '../auth/AuthContext';

/** Signed-in admin changes their own password. The API revokes every session, so we sign out on success. */
export function ChangePasswordModal({ open, onClose }: { open: boolean; onClose(): void }) {
  const [form] = Form.useForm();
  const [busy, setBusy] = useState(false);
  const { message } = App.useApp();
  const { logout } = useAuth();

  return (
    <Modal
      title="Change password"
      open={open}
      onCancel={onClose}
      okText="Change password"
      okButtonProps={{ loading: busy }}
      onOk={() => form.submit()}
      destroyOnHidden
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={async (v: { currentPassword: string; newPassword: string }) => {
          setBusy(true);
          try {
            await api.post('/auth/admin/password', { currentPassword: v.currentPassword, newPassword: v.newPassword });
            message.success('Password changed — sign in with your new password');
            onClose();
            await logout();
          } catch (e) {
            message.error(errorMessage(e));
          } finally {
            setBusy(false);
          }
        }}
      >
        <Form.Item name="currentPassword" label="Current password" rules={[{ required: true, message: 'Enter your current password' }]}>
          <Input.Password autoComplete="current-password" />
        </Form.Item>
        <Form.Item
          name="newPassword"
          label="New password"
          rules={[
            { required: true, message: 'Enter a new password' },
            { min: 12, message: 'At least 12 characters' },
            { max: 200 },
          ]}
        >
          <Input.Password autoComplete="new-password" />
        </Form.Item>
        <Form.Item
          name="confirm"
          label="Confirm new password"
          dependencies={['newPassword']}
          rules={[
            { required: true, message: 'Repeat the new password' },
            ({ getFieldValue }) => ({
              validator: (_, value) =>
                !value || value === getFieldValue('newPassword') ? Promise.resolve() : Promise.reject(new Error('Passwords do not match')),
            }),
          ]}
        >
          <Input.Password autoComplete="new-password" />
        </Form.Item>
      </Form>
    </Modal>
  );
}
